import {createAction} from '@reduxjs/toolkit'
import {ExperimentNames} from '@wix/wix-events-commons-statics'
import {isEditor} from '../../../../commons/selectors/environment'
import {isShowcase} from '../../../../commons/selectors/view-mode'
import {
  getCalendarEvents,
  getDefaultReferenceDay,
  getMonthOfDate,
  getNextMonth,
  getPreviousMonth,
  getToday,
  isCalendarPageLoaded,
  isCalendarPageLoading,
  isDayInCurrentMonth,
} from '../selectors/calendar-layout'
import {createAsyncAction} from '../services/redux-toolkit'
import {CalendarLoading} from '../types/state'
import {loadCalendarEvents} from './events'

export const addCalendarMonth = createAsyncAction<void, void>('ADD_CALENDAR_MONTH', async (_, {dispatch, getState}) => {
  const referenceMonth = getNextMonth(getState())
  await dispatch(loadCalendar({referenceMonth, origin: CalendarLoading.NEXT}))
})

export const subCalendarMonth = createAsyncAction<void, void>('SUB_CALENDAR_MONTH', async (_, {dispatch, getState}) => {
  const referenceMonth = getPreviousMonth(getState())
  await dispatch(loadCalendar({referenceMonth, origin: CalendarLoading.PREVIOUS}))
})

export const resetCalendar = createAsyncAction<void, boolean>(
  'RESET_CALENDAR',
  async (fullReset, {dispatch, getState}) => {
    const state = getState()

    let referenceMonth
    if (!isEditor(state)) {
      const today = getToday(state)
      const startOfMonth = getMonthOfDate(state, today)
      referenceMonth = startOfMonth ?? today
    }

    await dispatch(loadCalendar({referenceMonth, origin: CalendarLoading.DEFAULT, reset: fullReset}))
  },
)

interface LoadCalendarParams {
  referenceMonth?: string
  referenceDay?: string
  origin?: CalendarLoading
  reset?: boolean
}

export const loadCalendar = createAsyncAction<void, LoadCalendarParams>(
  'LOAD_CALENDAR',
  async ({referenceMonth, referenceDay, origin, reset}, {getState, dispatch, extra: {flowAPI}}) => {
    const calendarRevampEnabled = flowAPI.experiments.enabled(ExperimentNames.CalendarRevamp)
    const state = getState()

    const showcase = isShowcase(state)

    if (!calendarRevampEnabled && isCalendarPageLoading(state)) {
      return
    }

    if (
      calendarRevampEnabled &&
      !reset &&
      referenceMonth &&
      isCalendarPageLoaded(state.calendarLayout, referenceMonth)
    ) {
      referenceDay && dispatch(setCalendarReferenceDay(referenceDay))
      dispatch(setCalendarReferenceMonth(getMonthOfDate(state, referenceMonth) ?? referenceMonth))

      return
    }

    const response = await dispatch(loadCalendarEvents({referenceMonth, origin, fullReset: reset})).unwrap()

    const defaultReferenceDay = getDefaultReferenceDay(showcase, response.dates, state)

    calendarRevampEnabled && dispatch(setCalendarReferenceDay(referenceDay ?? defaultReferenceDay))

    if (!calendarRevampEnabled && !response) {
      dispatch(setCalendarReferenceMonth(getMonthOfDate(state, referenceMonth) ?? referenceMonth))
    }
  },
  {
    condition: (_, {getState, extra: {flowAPI}}) => {
      const calendarRevampEnabled = flowAPI.experiments.enabled(ExperimentNames.CalendarRevamp)
      const state = getState()

      if (calendarRevampEnabled) {
        return !isCalendarPageLoading(state)
      }

      return true
    },
  },
)

interface ChangeCalendarCategoryParams {
  veloCategoryId: string
  oldVeloCategoryId: string
  referenceMonth: string
  referenceDay: string
}

export const changeCalendarCategory = createAsyncAction<void, ChangeCalendarCategoryParams>(
  'CHANGE_CALENDAR_CATEGORY',
  async ({referenceMonth, referenceDay}, {dispatch}) => {
    await dispatch(
      loadCalendar({
        referenceMonth,
        referenceDay,
        reset: true,
        origin: CalendarLoading.DEFAULT,
      }),
    ).unwrap()
  },
  {
    condition: ({veloCategoryId, oldVeloCategoryId}) => {
      return veloCategoryId !== oldVeloCategoryId
    },
  },
)

export const setCalendarReferenceDay = createAction<string>('SET_CALENDAR_REFERENCE_DAY')

export const setCalendarReferenceMonth = createAction<string>('SET_CALENDAR_REFERENCE_MONTH')

export const openMonthlyCalendarPopup = createAction<string>('OPEN_MONTHLY_CALENDAR_POPUP')

export const closeMonthlyCalendarPopup = createAction('CLOSE_MONTHLY_CALENDAR_POPUP')

export const openMonthlyCalendarEvent = createAction<{eventId: string; selectedDate?: string}>(
  'OPEN_MONTHLY_CALENDAR_EVENT',
)

export const closeMonthlyCalendarEvent = createAction('CLOSE_MONTHLY_CALENDAR_EVENT')

export const openAnyEventDetails = createAsyncAction<void, void>(
  'OPEN_ANY_EVENT_DETAILS',
  async (_, {dispatch, getState}) => {
    const state = getState()
    const events = getCalendarEvents(state)

    const day = Object.keys(events).find(dayWithEvents => isDayInCurrentMonth(state, dayWithEvents))

    if (day) {
      const eventId = events[day][0]
      dispatch(openMonthlyCalendarEvent({eventId, selectedDate: day}))
    }
  },
)

export const openAnyEventList = createAsyncAction<void, void>(
  'OPEN_ANY_EVENT_LIST',
  async (_, {dispatch, getState}) => {
    const state = getState()
    const events = getCalendarEvents(state)

    const dayWithMoreThanOneEvent = Object.keys(events).find(
      dayWithEvents => isDayInCurrentMonth(state, dayWithEvents) && events[dayWithEvents].length > 1,
    )
    const dayWithOneEvent = Object.keys(events).find(
      dayWithEvents => isDayInCurrentMonth(state, dayWithEvents) && events[dayWithEvents].length === 1,
    )

    if (dayWithMoreThanOneEvent) {
      dispatch(openMonthlyCalendarPopup(dayWithMoreThanOneEvent))
    } else if (dayWithOneEvent) {
      dispatch(openMonthlyCalendarPopup(dayWithOneEvent))
    }
  },
)
